import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Dumbbell,
  RotateCcw,
  Bookmark,
  FileQuestion,
  CalendarCheck,
  CalendarClock,
  Layers,
  BookOpenCheck,
  GraduationCap,
  LogOut,
  Menu,
  X,
  LockKeyhole,
} from "lucide-react";
import { useStudyAuth } from "@/context/StudyAuth";
import { STUDY } from "@/constants/testIds";

const DISCLAIMER =
  "Illinois Broker Prep is an independent study aid. Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission. No actual exam questions. No pass guarantee.";

const NAV = [
  { to: "/study/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/study/learn", label: "Learn", icon: GraduationCap, testId: STUDY.navLearn },
  { to: "/study/plan", label: "Study Plan", icon: CalendarCheck, testId: STUDY.navStudyPlan, premium: true },
  { to: "/study/exam-week", label: "Exam Week", icon: CalendarClock, premium: true },
  { to: "/study/question-bank", label: "Question Bank", icon: BookOpenCheck, testId: STUDY.navQuestionBank },
  { to: "/study/practice", label: "Practice Sets", icon: Dumbbell },
  { to: "/study/mini-exams", label: "Exam Mode", icon: FileQuestion, testId: STUDY.navExamMode, premium: true },
  { to: "/study/flashcards", label: "Word Bank", icon: Layers, testId: STUDY.navFlashcards },
  { to: "/study/missed", label: "Missed", icon: RotateCcw },
  { to: "/study/bookmarks", label: "Bookmarks", icon: Bookmark },
];

export default function StudyShell({ children, testId }) {
  const { isAuthed, hasFullAccess, logout } = useStudyAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const activePage = NAV.find(({ to }) => location.pathname.startsWith(to))?.label || "Study tools";

  // Best-effort no-print guard for paid study pages (blocks Ctrl/Cmd+P).
  // Honest limitation: cannot stop screenshots/photos/manual copying.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  if (!isAuthed) return <Navigate to="/study/login" replace />;

  return (
    <div data-testid={testId} className="flex min-h-screen flex-col bg-[var(--ibp-offwhite)]">
      <header className="relative z-30 bg-[var(--ibp-navy-950)] text-white">
        <div className="mx-auto max-w-[1500px] px-3 py-2 sm:px-5">
          <div className="flex items-center justify-between gap-3 2xl:hidden">
            <Link to="/study/dashboard" className="min-w-0">
              <span className="block text-sm font-bold">Illinois Broker Prep</span>
              <span className="block truncate text-xs text-white/65">{activePage}</span>
            </Link>
            <button
              type="button"
              aria-label={mobileMenuOpen ? "Close study menu" : "Open study menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/20 text-white transition-colors hover:bg-white/10"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {mobileMenuOpen ? (
            <div className="border-t border-white/10 pb-2 pt-3 2xl:hidden">
              <nav className="grid grid-cols-2 gap-1">
                {NAV.map(({ to, label, icon: Icon, premium }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      [
                        "inline-flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                        isActive
                          ? "bg-white text-[var(--ibp-navy-950)]"
                          : "text-white/85 hover:bg-white/10",
                      ].join(" ")
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" /> {label}
                    {premium && !hasFullAccess ? <LockKeyhole className="ml-auto h-3.5 w-3.5 text-[var(--ibp-gold-500)]" /> : null}
                  </NavLink>
                ))}
              </nav>
              <button
                type="button"
                data-testid={STUDY.logout}
                onClick={() => {
                  logout();
                  navigate("/study/login");
                }}
                className="mt-2 inline-flex min-h-10 w-full items-center gap-2 rounded-lg border-t border-white/10 px-3 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
              >
                <LogOut className="h-4 w-4" /> Log out
              </button>
            </div>
          ) : null}

          <div className="hidden items-center gap-2 2xl:flex">
            <nav className="flex min-w-0 flex-1 items-center gap-1">
              {NAV.map(({ to, label, icon: Icon, testId, premium }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    [
                      "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-colors",
                      isActive
                        ? "bg-white text-[var(--ibp-navy-950)]"
                        : "text-white/85 hover:bg-white/10",
                    ].join(" ")
                  }
                >
                  <Icon className="h-4 w-4" /> {label}
                  {premium && !hasFullAccess ? <LockKeyhole className="h-3 w-3 text-[var(--ibp-gold-500)]" /> : null}
                </NavLink>
              ))}
            </nav>
            <button
              type="button"
              aria-label="Log out"
              data-testid={STUDY.logout}
              onClick={() => {
                logout();
                navigate("/study/login");
              }}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      {!hasFullAccess ? (
        <div className="border-b border-[color:rgba(10,27,51,0.1)] bg-white">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-xs text-[color:rgba(11,18,32,0.64)] sm:justify-between sm:text-left">
            <span><strong className="text-[var(--ibp-ink)]">Free account:</strong> 2 lessons, 25 practice questions, and 40 Word Bank terms.</span>
            <Link to="/product" className="inline-flex items-center gap-1 font-semibold text-[#0F3D8A] hover:underline">
              Unlock the complete platform <LockKeyhole className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      ) : null}

      <main className="relative z-10 mx-auto w-full max-w-4xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        {children}
      </main>

      <footer className="border-t border-[color:rgba(10,27,51,0.1)] py-6">
        <p className="mx-auto max-w-3xl px-4 text-center text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
          {DISCLAIMER}
        </p>
        <p className="mt-2 text-center text-xs">
          <Link to="/study/feedback" className="font-semibold text-[#0F3D8A] hover:underline">
            Share product feedback
          </Link>
        </p>
      </footer>
    </div>
  );
}
